import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';
import { nextRequestToken, isStaleRequest, loadStart, loadSettle } from './shared';

export const createOwnersSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // Owner Management with API integration
  fetchOwners: async (condoId?: string) => {
    const token = nextRequestToken('owners');
    set(loadStart('owners')); // 106.1
    try {
      const owners = await apiService.getOwners(condoId);
      // 94.4 — a newer fetch superseded this one (e.g. condo switch);
      // discard so we never overwrite the current condo's owners.
      if (isStaleRequest(token)) return;
      // A successful (2xx) response — even an empty list — is real data.
      set(state => ({ ...loadSettle('owners', null)(state), owners }));
    } catch (error: any) {
      if (isStaleRequest(token)) return;
      // Sprint 81 (ADR-081) — do NOT swallow a failed fetch into an empty
      // table. Log the endpoint context to the console and surface the error
      // state so the screen renders an honest error, not a false "No data".
      // We deliberately leave `owners` untouched (no `set({ owners: [] })`)
      // so a transient failure doesn't wipe previously-loaded data.
      console.error(
        `[condoStore.fetchOwners] GET /owners/condo/${condoId ?? '(none)'} failed:`,
        error?.message || error,
      );
      set(loadSettle('owners', error.message || 'Failed to fetch owners'));
    }
  },

  addOwner: (owner) =>
    set((state) => ({
      owners: [...state.owners, { ...owner, moveInDate: new Date(owner.moveInDate) }]
    })),

  createOwner: async (ownerData) => {
    const { setLoading, setError, fetchOwners } = get();
    setLoading(true);
    try {
      await apiService.createOwner(ownerData);
      await fetchOwners(ownerData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create owner');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateOwner: (id, updatedOwner) =>
    set((state) => ({
      owners: state.owners.map((owner) =>
        owner.id === id ? { ...owner, ...updatedOwner } : owner
      ),
    })),

  updateOwnerApi: async (id, ownerData) => {
    const { setLoading, setError, getOwnerById, fetchOwners } = get();
    setLoading(true);
    try {
      await apiService.updateOwner(id, ownerData);
      const owner = getOwnerById(id);
      await fetchOwners(owner?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update owner');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteOwner: (id) =>
    set((state) => ({
      owners: state.owners.filter((owner) => owner.id !== id),
      fees: state.fees.filter((fee) => fee.ownerId !== id),
    })),

  deleteOwnerApi: async (id) => {
    const { setLoading, setError, getOwnerById, fetchOwners } = get();
    setLoading(true);
    try {
      const owner = getOwnerById(id);
      await apiService.deleteOwner(id);
      await fetchOwners(owner?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete owner');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  getOwnerByUnit: (unitNumber, condoId) => {
    const { owners } = get();
    // Sprint 116 (116.4) — a unitNumber is unique only WITHIN a condo, so a
    // global match across tenants could return a *different* condo's owner.
    // Always scope to a condo. Prefer the explicit arg; otherwise infer the
    // active condo from the loaded owner set (fetches are per-condo, so `owners`
    // holds the active condo's roster). If the roster spans multiple condos and
    // no condoId was given, refuse to guess and return undefined.
    let scopeId = condoId;
    if (!scopeId) {
      const distinct = Array.from(new Set(owners.map(o => o.condoId)));
      if (distinct.length !== 1) return undefined;
      scopeId = distinct[0];
    }
    return owners.find(
      owner => owner.unitNumber === unitNumber && owner.condoId === scopeId,
    );
  },

  getOwnerById: (id) =>
    get().owners.find(owner => owner.id === id),
});
